import { readCookie } from "@zapier/cookies";
import { type ReactNode, useEffect, useState } from "react";

import { CurrentSsoHintContext } from "@/context/current-sso-hint";

/** This provider and set of hooks returns an updated value for the ssohint cookie whenever
 * it changes locally.
 *
 * This cookie is used to determine if the user has been logged out in another Zapier session.
 * An "anonymous" value in the cookie indicates that the user has been logged out.
 */

function getSsoHintFromCookie() {
  if (typeof document === "undefined") {
    return;
  }
  return readCookie("ssohint");
}

interface CurrentSsoHintProviderProps {
  children: ReactNode;
}

export function CurrentSsoHintProvider(props: CurrentSsoHintProviderProps) {
  const [currentSsoHint, setCurrentSsoHint] = useState<string | undefined>(
    getSsoHintFromCookie(),
  );

  useEffect(() => {
    /** Watch current account id cookie for changes */
    async function watchCookie() {
      const ssoHintFromCookie = getSsoHintFromCookie();
      // Only take action if this is a real change
      if (ssoHintFromCookie && ssoHintFromCookie !== currentSsoHint) {
        // Account id has changed
        setCurrentSsoHint(ssoHintFromCookie);
      }
    }

    // Read cookie value on a set interval to watch for changes
    const timer = setInterval(watchCookie, 1000);

    /** Remove cookie watcher when we dismount */
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentSsoHint]);

  /** Create provider */
  return (
    <CurrentSsoHintContext.Provider value={currentSsoHint}>
      {props.children}
    </CurrentSsoHintContext.Provider>
  );
}
